import { visitJobId, type CustomerJob, type JobKind } from './jobs'
import { effectiveCycle, isMissed, isSuggestion, isUnplaced, missedVisit, needsReview, noFixedCycle, pushedNow, rebooked, upcomingVisits } from './planning'
import { cyclesOn } from './round-config'
import type { Clean, Customer, SchedulePayload } from './types'

// The diary's sets, per JOB (several jobs, PLAN 4; Astra named them): which customers' work is waiting in a week
// with no day (inSheet), who already has a real visit ahead (placed), who Due this week collects (and so which
// suggestions leave the day cards), and who the review box asks about. Pure, so the paired rehearsal and the tests
// read the same rules the diary draws.
//
// Every set is keyed by job (jobKey): a customer's windows and gutters are two entries. Job 1 is the customer row
// itself (its aggregates on GET /api/customers are job 1's); each running extra job with a repeat becomes a
// customer-shaped DueEntry carrying THAT job's dates, price and messages (GET /api/customer-jobs), so the diary's
// due rows, placements and badges work on it unchanged. On a round with several jobs off there are no extra jobs,
// every visit is job 1's, and every set is exactly the customer-wide one it always was.

export interface DueJob {
  id: number
  name: string
  kind: JobKind
  version: number
}

// A row Due this week can hold: a customer (job 1), or one of their extra jobs dressed as one.
export type DueEntry = Customer & { dueJob?: DueJob }

export function jobKey(customerId: number, jobId: number | null): string {
  return `${customerId}:${jobId ?? 0}`
}

export function visitKey(v: Pick<Clean, 'customer_id'> & { job_id?: number | null }): string {
  return jobKey(v.customer_id, visitJobId(v))
}

export function dueKey(c: DueEntry): string {
  return jobKey(c.id, c.dueJob?.id ?? null)
}

// An extra job as a due row: the customer's own details (name, address, status, follow-up), with the job's
// price, how often, last visit, next visit, missed visit and Messages count in the customer fields the diary
// reads. Only a RUNNING job with a repeat: a one-off or a stopped job is never due.
export function jobEntry(c: Customer, j: CustomerJob): DueEntry | null {
  if (!j.cycle_days || j.cycle_days <= 0) return null
  return jobEntryAny(c, j)
}

// The same for any running job, a one-off too: it is never DUE (no repeat), but a visit planned for it still needs
// agreeing and a missed one following up (Messages; Astra r1, finding 9).
export function jobEntryAny(c: Customer, j: CustomerJob): DueEntry | null {
  if (j.deleted_at || j.stopped_on || j.customer_id !== c.id) return null
  return {
    ...c,
    reg_price_pence: j.price_pence ?? null,
    first_clean_price_pence: null,
    inside_price_pence: null,
    cycle_days: j.cycle_days && j.cycle_days > 0 ? j.cycle_days : null,
    cycle_text: j.cycle_text ?? null,
    cycle_estimate_days: null,
    last_clean: j.last_clean ?? null,
    last_clean_id: j.last_clean_id ?? null,
    next_booked: j.next_booked ?? null,
    next_booked_id: j.next_booked_id ?? null,
    next_booked_version: j.next_booked_version ?? null,
    next_booked_time_slot: j.next_booked_time_slot,
    next_booked_time_agreed_for: j.next_booked_time_agreed_for,
    next_booked_source: j.next_booked_source ?? null,
    next_booked_created_by: j.next_booked_created_by ?? null,
    next_booked_confirmed: (j.next_booked_confirmed ?? null) as 0 | 1 | null,
    next_confirmed: j.next_confirmed ?? null,
    has_unconfirmed: (j.has_unconfirmed ?? null) as 0 | 1 | null,
    overdue_bookings: j.overdue_bookings ?? 0,
    missed_visit_id: j.missed_visit_id ?? null,
    missed_visit_version: j.missed_visit_version ?? null,
    missed_visit_confirmed: j.missed_visit_confirmed,
    missed_visit_time_slot: j.missed_visit_time_slot,
    missed_visit_time_agreed_for: j.missed_visit_time_agreed_for,
    missed_visit_date: j.missed_visit_date ?? null,
    rebook_messages: j.rebook_messages ?? 0,
    rebook_messaged_on: j.rebook_messaged_on ?? null,
    due_pushed_until: j.due_pushed_until ?? null,
    due_pushes: j.due_pushes ?? 0,
    dueJob: { id: j.id, name: j.name, kind: j.kind, version: j.version },
  }
}

// Job 1 of every customer whose job 1 still runs, then each running extra job, in customer order.
export function jobEntries(customers: Customer[], jobs: CustomerJob[] | null | undefined): DueEntry[] {
  const byCustomer = new Map<number, CustomerJob[]>()
  for (const j of jobs ?? []) {
    const list = byCustomer.get(j.customer_id) ?? []
    list.push(j)
    byCustomer.set(j.customer_id, list)
  }
  const out: DueEntry[] = []
  for (const c of customers) {
    if (!c.job_stopped_on) out.push(c)
    for (const j of (byCustomer.get(c.id) ?? []).sort((a, b) => a.id - b.id)) {
      const e = jobEntry(c, j)
      if (e) out.push(e)
    }
  }
  return out
}

// A missed visit the job has been done since (on that day or after) is a record, not work still waiting: the customer
// was seen to (bug hunt, 8 Oct 2026: Yusuf, cleaned today, came straight back into To place as "was due 4 Oct, not
// done"). It stays in Past visits to review until someone says what happened that day.
function settledByLaterClean(c: Pick<DueEntry, 'last_clean'>, missed: Pick<Clean, 'date'>): boolean {
  return !!c.last_clean && missed.date <= c.last_clean
}

// The job's missed visit, from the payload's overdue list (booked, past, not a contact reminder), oldest first; not one
// the job has been done since.
export function missedCleanFor(c: DueEntry, p: Pick<SchedulePayload, 'overdue'>, today: string): Clean | null {
  const job = c.dueJob?.id ?? null
  return p.overdue?.find((cl) => cl.customer_id === c.id && visitJobId(cl) === job && isMissed(cl, today) && !settledByLaterClean(c, cl)) ?? null
}

// A missed visit still waiting for a new day: none ahead of it (rebooked), not done since, and answered ("Not done >
// Book them again" marks it reviewed). Without the overdue list (an older saved copy) the customer's own count
// decides, as before: over-asking beats going quiet.
function missedWaiting(c: DueEntry, p: Pick<SchedulePayload, 'overdue'>, today: string, evenUnanswered = false): boolean {
  if (!missedVisit(c) || rebooked(c)) return false
  if (!p.overdue) return true
  const missed = missedCleanFor(c, p, today)
  return !!missed && (evenUnanswered || !!missed.reviewed_on)
}

// A missed visit nobody has answered yet is asked about first (Past visits to review, Owed's "Done?"), and the
// customer stays off To place meanwhile: they are still on that day, and may well have been cleaned (Liam, 9 Oct 2026:
// "alexandra clarke was placed on thursday but was still in the to place column"). Once answered they come back.
function missedUnanswered(c: DueEntry, p: Pick<SchedulePayload, 'overdue'>, today: string): boolean {
  if (!p.overdue || !missedVisit(c) || rebooked(c)) return false
  const missed = missedCleanFor(c, p, today)
  return !!missed && !missed.reviewed_on
}

// Their next visit is the app's own unagreed suggestion dated inside the week on screen, and no other future visit
// of THE SAME JOB is in view.
export function dueCarrier(c: DueEntry, p: Pick<SchedulePayload, 'from' | 'to' | 'cleans'>, today: string): boolean {
  if (!c.next_booked || c.next_booked_source !== 'suggested' || c.next_booked_confirmed) return false
  if (c.next_booked < p.from || c.next_booked > p.to) return false
  const job = c.dueJob?.id ?? null
  return !p.cleans.some((cl) =>
    cl.customer_id === c.id && visitJobId(cl) === job && cl.id !== c.next_booked_id &&
    cl.state === 'booked' && cl.date > today && !isSuggestion(cl))
}

// The jobs with work waiting in a week of the payload with no day chosen (they sit in "No day yet").
export function inSheetKeys(p: Pick<SchedulePayload, 'from' | 'to' | 'cleans'>): Set<string> {
  return new Set(p.cleans.filter((c) => isUnplaced(c, p.from, p.to)).map(visitKey))
}

// Due (Liam, 11 Oct 2026; docs/plans/diary-due/PLAN.md), per job. Only a job with a cycle SET on it ("if they have a
// cycle i could see being in due"; ad hoc and once-or-twice-a-year customers stay out, and so does a cycle Roundly only
// guessed from past visits, which for Dropby was mostly the irregular ones). In: a missed visit waiting for a new day;
// nothing booked and the due date on or before the end of the week on screen; or the app's own suggestion inside that
// week (dueCarrier). Out: not active, work already held in a week with no day, asked to wait ("Not now, ask again"),
// a missed visit nobody has answered yet (Past visits to review asks first).
export type DueKind = 'missed' | 'due' | 'carrier' | 'first'

// Something booked ahead that is a visit, not Roundly's "ask them" reminder: a customer with no cycle gets one after each
// visit and keeps it once a cycle is set (round 2: 8 of Dropby's customers with no cycle had only that ahead, so setting
// their cycle from the guess would have left them out of Due). An extra job's dates are its own and carry no reminder.
// A reminder someone has agreed (confirmed) is a real visit, as everywhere else (planning.ts isContact).
export function bookedAhead(c: DueEntry): boolean {
  if (!c.next_booked) return false
  if (c.dueJob || c.next_booked_source !== 'tentative' || c.next_booked_confirmed) return true
  const ahead = upcomingVisits(c)
  return ahead === null || ahead.some((v) => (v.source !== 'tentative' || !!v.confirmed) && v.job_id == null)
}

export function setCycleDays(c: Pick<Customer, 'cycle_days'>): number | null {
  return c.cycle_days && c.cycle_days > 0 ? c.cycle_days : null
}

function dueKind(c: DueEntry, p: Pick<SchedulePayload, 'from' | 'to' | 'cleans' | 'overdue'>, today: string, inSheet: Set<string>,
  askAgainLater: (c: Customer) => boolean, evenUnanswered: boolean): DueKind | null {
  // On the Recanvass or Taken off list: Messages and Work look after them, not Due (round 2 review).
  if (c.status !== 'active' || c.customer_list || inSheet.has(dueKey(c))) return null
  if (askAgainLater(c)) return null
  const days = setCycleDays(c)
  // No cycle set: only a first visit (never visited, nothing booked, nothing missed), Needs you's old "No date yet" (round
  // 2: "No date yet is the First visit tab"). Not someone who said they would ask (ad hoc, on request, one off).
  if (!days) return !c.dueJob && !c.last_clean && !bookedAhead(c) && !(c.overdue_bookings ?? 0) && !noFixedCycle(c) ? 'first' : null
  if (!evenUnanswered && missedUnanswered(c, p, today)) return null
  if (missedWaiting(c, p, today, evenUnanswered)) return 'missed'
  if (!bookedAhead(c)) {
    // Never visited and nothing booked: a first visit, the list's own tab, not the bar (a big import would bury the
    // regulars). A booking already missed is not a first visit.
    if (!c.last_clean) return (c.overdue_bookings ?? 0) > 0 ? null : 'first'
    return cyclesOn(c.last_clean, days) <= p.to ? 'due' : null
  }
  return dueCarrier(c, p, today) ? 'carrier' : null
}

// The day a job was due from: its missed visit, else its cycle's due date, else (a first visit) none.
export function dueFrom(c: DueEntry, p: Pick<SchedulePayload, 'overdue'>, today: string): string | null {
  const missed = missedCleanFor(c, p, today)
  if (missed) return missed.date
  const days = setCycleDays(c)
  return c.last_clean && days ? cyclesOn(c.last_clean, days) : null
}

// How many of their cleans they are behind: days past the due day over the cycle. A 4-weekly customer two weeks late
// is half a clean behind; a yearly one two weeks late hardly any (Liam: "Good idea to have an alorith to sort who is
// due"). Negative while the due day is still to come.
export function cleansBehind(c: DueEntry, p: Pick<SchedulePayload, 'overdue'>, today: string): number {
  const from = dueFrom(c, p, today)
  const days = setCycleDays(c)
  if (!from || !days) return 0
  return (Date.parse(`${today}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86_400_000 / days
}

// More than this many cleans behind: Gone quiet, the end of the list, not the bar (Liam agreed, 11 Oct 2026: Dropby had
// 37 due with nothing booked, 23 of them more than two cleans behind and likely stopped).
export const GONE_QUIET_CLEANS = 2
// The bar shows this many; the rest are a "+ N more" card away (Liam: "Raise the amount ... make it clear there is more").
export const DUE_BAR_MAX = 20

export interface DueGroups {
  due: DueEntry[]         // the bar, most behind first
  goneQuiet: DueEntry[]   // more than GONE_QUIET_CLEANS behind, least gone first
  firstVisit: DueEntry[]  // never visited, nothing booked (a cycle set, or none agreed yet)
  nextWeek: DueEntry[]    // pushed out until next week (due_pushed_until)
}

export function dueGroups(
  p: Pick<SchedulePayload, 'from' | 'to' | 'cleans' | 'overdue'>,
  customers: Customer[] | null,
  jobs: CustomerJob[] | null | undefined,
  today: string,
  askAgainLater: (c: Customer) => boolean,
  evenUnanswered = false,
): DueGroups {
  const out: DueGroups = { due: [], goneQuiet: [], firstVisit: [], nextWeek: [] }
  if (!customers) return out
  const inSheet = inSheetKeys(p)
  const behind = new Map<string, number>()
  for (const c of jobEntries(customers, jobs)) {
    const kind = dueKind(c, p, today, inSheet, askAgainLater, evenUnanswered)
    if (!kind) continue
    if (pushedNow(c, today)) out.nextWeek.push(c)
    else if (kind === 'first') out.firstVisit.push(c)
    else {
      const b = cleansBehind(c, p, today)
      behind.set(dueKey(c), b)
      ;(b > GONE_QUIET_CLEANS ? out.goneQuiet : out.due).push(c)
    }
  }
  const tie = (a: DueEntry, b: DueEntry): number => a.id - b.id || (a.dueJob?.id ?? 0) - (b.dueJob?.id ?? 0)
  // Late ones first, most behind first; then the ones still to come, soonest due first (a fraction of a cycle still to
  // go would put a yearly customer due in three days before a 4-weekly one due in two).
  const late = (c: DueEntry): number => behind.get(dueKey(c)) ?? 0
  const from = (c: DueEntry): string => dueFrom(c, p, today) ?? '9999-12-31'
  out.due.sort((a, b) => (late(b) > 0 ? 1 : 0) - (late(a) > 0 ? 1 : 0)
    || (late(a) > 0 ? late(b) - late(a) : from(a).localeCompare(from(b))) || tie(a, b))
  out.goneQuiet.sort((a, b) => (behind.get(dueKey(a)) ?? 0) - (behind.get(dueKey(b)) ?? 0) || tie(a, b))
  out.firstVisit.sort((a, b) => a.name.localeCompare(b.name) || tie(a, b))
  out.nextWeek.sort((a, b) => (a.due_pushed_until ?? '').localeCompare(b.due_pushed_until ?? '') || a.name.localeCompare(b.name) || tie(a, b))
  return out
}

// No cycle set (Liam, 11 Oct 2026: "guessed cycle shoudnt count but guessed cycle shoulf allow almost one or tow clicks
// to update"): customers with no cycle set whose date by Roundly's guess from their past visits has come, nothing booked
// and nothing missed. They are not in Due; the list's own tab offers the guess in one tap ("Set every 8 weeks"), which
// brings them into Due by the rules above. Newest due first: the ones most likely still regulars lead.
export function noCycleSet(
  p: Pick<SchedulePayload, 'from' | 'to' | 'cleans'>,
  customers: Customer[] | null,
  today: string,
  askAgainLater: (c: Customer) => boolean,
): { customer: Customer; guessDays: number; due: string }[] {
  const inSheet = inSheetKeys(p)
  const out: { customer: Customer; guessDays: number; due: string }[] = []
  for (const c of customers ?? []) {
    if (c.status !== 'active' || c.customer_list || c.job_stopped_on || setCycleDays(c) || !c.last_clean || bookedAhead(c) || (c.overdue_bookings ?? 0) > 0) continue
    if (inSheet.has(dueKey(c)) || askAgainLater(c) || pushedNow(c, today)) continue
    const cycle = effectiveCycle(c)
    if (!cycle.guessed || !cycle.days) continue
    const due = cyclesOn(c.last_clean, cycle.days)
    if (due <= p.to) out.push({ customer: c, guessDays: cycle.days, due })
  }
  return out.sort((a, b) => b.due.localeCompare(a.due) || a.customer.name.localeCompare(b.customer.name) || a.customer.id - b.customer.id)
}

// Everyone waiting for a day (the bar, Gone quiet and First visit; not those pushed to next week), per job: what the
// diary collects off the day cards and what placing reads.
export function dueEntries(
  p: Pick<SchedulePayload, 'from' | 'to' | 'cleans' | 'overdue'>,
  customers: Customer[] | null,
  jobs: CustomerJob[] | null | undefined,
  today: string,
  askAgainLater: (c: Customer) => boolean,
  // Who Due WOULD take once their missed visit is answered (the Past visits card's "Book them again").
  evenUnanswered = false,
): DueEntry[] {
  const g = dueGroups(p, customers, jobs, today, askAgainLater, evenUnanswered)
  return [...g.due, ...g.goneQuiet, ...g.firstVisit]
}

// The jobs with a real visit already in the diary ahead of them (planning.rebooked), so their missed visit is a
// record, not a job: the review box leaves it out.
export function placedKeys(customers: Customer[] | null, jobs: CustomerJob[] | null | undefined): Set<string> {
  return new Set(jobEntriesAll(customers ?? [], jobs).filter(rebooked).map(dueKey))
}

// Like jobEntries, but a stopped job still answers for its own missed visits (it is never due, but a human visit
// ahead of it still settles the old one).
function jobEntriesAll(customers: Customer[], jobs: CustomerJob[] | null | undefined): DueEntry[] {
  const out: DueEntry[] = [...customers]
  const byId = new Map(customers.map((c) => [c.id, c]))
  for (const j of jobs ?? []) {
    const c = byId.get(j.customer_id)
    if (!c || j.deleted_at) continue
    const e = jobEntry(c, { ...j, stopped_on: null, cycle_days: j.cycle_days && j.cycle_days > 0 ? j.cycle_days : 1 })
    if (e) out.push(e)
  }
  return out
}

// "Past bookings to review": the missed visits nobody has answered for, less those whose job is placed.
export function reviewVisits(overdue: Clean[], customers: Customer[] | null, jobs: CustomerJob[] | null | undefined, today: string): Clean[] {
  const placed = placedKeys(customers, jobs)
  return overdue.filter((c) => needsReview(c, today) && !placed.has(visitKey(c)))
}

// What Due this week collects off the day cards: the app's own suggestions of the jobs it holds. Per job, so
// gutters in the panel never take the windows' suggestion with them.
export function collectedFilter(due: DueEntry[]): (c: Clean) => boolean {
  const collected = new Set(due.map(dueKey))
  return (c: Clean): boolean => !(collected.has(visitKey(c)) && isSuggestion(c))
}
